import { Link } from 'react-router-dom';
import { GraduationCap, Megaphone, Globe2, Settings2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Layout from '@/components/Layout';
import Reveal from '@/components/Reveal';
import SEO from '@/components/SEO';
import { SectionLabel, PrimaryButton, TextLink } from '@/components/Button';
import StatStrip from '@/components/StatStrip';
import ArticleCard from '@/components/ArticleCard';
import Faq from '@/components/Faq';
import { INSIGHT_META, IMAGES } from '@/data/insights';

const SERVICE_ICONS = [GraduationCap, Megaphone, Globe2, Settings2];

export default function Home() {
  const { t } = useTranslation(['home', 'common']);
  const services = t('services.cards', { returnObjects: true });
  const latest = INSIGHT_META.slice(0, 3);

  return (
    <Layout transparentNav>
      <SEO title={t('seo.title')} description={t('seo.description')} />

      {/* HERO */}
      <section
        className="relative min-h-[68vh] md:min-h-[72vh] max-h-[760px] w-full overflow-hidden"
        data-testid="home-hero"
      >
        <img
          src={IMAGES.hero}
          alt={t('hero.heroImgAlt')}
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
          onError={(e) => {
            e.currentTarget.src = IMAGES.heroAlt;
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1220]/70 via-[#0B1220]/45 to-[#0B1220]/75" aria-hidden="true" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1220]/55 via-[#0B1220]/20 to-transparent" aria-hidden="true" />

        <div className="relative max-w-content mx-auto px-6 md:px-8 pt-28 pb-14 md:pt-32 md:pb-20">
          <div className="grid grid-cols-1 md:grid-cols-12 w-full">
            <div className="md:col-span-9 lg:col-span-8">
              <p
                className="text-[11px] md:text-[12px] uppercase font-medium text-[#D9B988] mb-6"
                style={{ letterSpacing: '0.22em' }}
              >
                {t('hero.eyebrow')}
              </p>
              <h1 className="font-display text-[36px] sm:text-[44px] lg:text-[52px] xl:text-[58px] font-semibold text-white leading-[1.06] tracking-tight">
                {t('hero.headline')}
              </h1>
              <p className="mt-6 text-white/85 text-base md:text-lg leading-[1.7] max-w-2xl">
                {t('hero.sub')}
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-2 bg-white text-brand-primary px-6 py-3 text-sm font-medium tracking-wide hover:bg-[#D9B988] transition-colors duration-200 whitespace-nowrap"
                  data-testid="hero-primary-cta"
                >
                  {t('cta.letsTalk', { ns: 'common' })}
                  <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
                </Link>
                <Link
                  to="/services"
                  className="group inline-flex items-center gap-2 border border-white/60 text-white px-6 py-3 text-sm font-medium tracking-wide hover:bg-white hover:text-brand-primary transition-colors duration-200 whitespace-nowrap"
                  data-testid="hero-secondary-cta"
                >
                  {t('cta.seeHowIWork', { ns: 'common' })}
                  <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* THE CHALLENGE */}
      <section className="py-24 md:py-32" data-testid="home-challenge">
        <div className="max-w-content mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <SectionLabel>{t('challenge.label')}</SectionLabel>
              <h2 className="mt-5 font-display text-3xl md:text-[40px] font-semibold text-brand-primary leading-tight tracking-tight">
                {t('challenge.headline')}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <Reveal delay={0.05}>
              <p className="text-[17px] leading-[1.8] text-brand-text max-w-[68ch]">
                {t('challenge.body')}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32 bg-brand-surface" data-testid="home-services">
        <div className="max-w-content mx-auto px-6 md:px-8">
          <Reveal>
            <div className="max-w-3xl">
              <SectionLabel>{t('services.label')}</SectionLabel>
              <h2 className="mt-5 font-display text-3xl md:text-[44px] font-semibold text-brand-primary leading-tight tracking-tight">
                {t('services.headline')}
              </h2>
            </div>
          </Reveal>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {services.map((s, i) => {
              const Icon = SERVICE_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.05}>
                  <div
                    className="group p-6 md:p-8 h-full bg-brand-background border border-black/10 rounded-none flex flex-col transition-all duration-250 ease-out hover:-translate-y-1 hover:shadow-xl"
                    data-testid={`service-card-${i}`}
                  >
                    <Icon size={22} strokeWidth={1.25} className="text-brand-accent" />
                    <h3 className="mt-5 font-display text-[22px] md:text-[24px] font-semibold text-brand-primary leading-snug tracking-tight">
                      {s.title}
                    </h3>
                    <p className="mt-4 text-[15px] text-brand-text leading-[1.75]">{s.body}</p>
                    <div className="mt-auto pt-6">
                      <Link
                        to="/services"
                        className="text-sm font-medium text-brand-accent link-underline"
                        data-testid={`service-card-link-${i}`}
                      >
                        {t('cta.learnMore', { ns: 'common' })} →
                      </Link>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-14 max-w-3xl">
              <p className="text-[17px] text-brand-text leading-[1.8]">{t('services.paragraph')}</p>
              <div className="mt-8">
                <TextLink to="/services" testId="services-cta">{t('cta.seeHowIWork', { ns: 'common' })} →</TextLink>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROOF STRIP */}
      <section className="py-16 md:py-20" data-testid="home-proof">
        <div className="max-w-content mx-auto px-6 md:px-8">
          <Reveal>
            <StatStrip />
          </Reveal>
        </div>
      </section>

      {/* CASE STUDY TEASER */}
      <section className="py-24 md:py-32" data-testid="home-case-study">
        <div className="max-w-content mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-6">
            <Reveal>
              <SectionLabel>{t('caseStudy.label')}</SectionLabel>
              <h2 className="mt-5 font-display text-3xl md:text-[40px] font-semibold text-brand-primary leading-tight tracking-tight">
                {t('caseStudy.headline')}
              </h2>
              <p className="mt-6 text-[17px] text-brand-text leading-[1.8] max-w-[68ch]">{t('caseStudy.body.0')}</p>
              <p className="mt-5 text-[17px] text-brand-text leading-[1.8] max-w-[68ch]">{t('caseStudy.body.1')}</p>
              <p className="mt-5 text-[17px] text-brand-text leading-[1.8] max-w-[68ch]">{t('caseStudy.body.2')}</p>
              <div className="mt-8">
                <TextLink to="/work" testId="case-study-cta">{t('cta.seeMoreWork', { ns: 'common' })} →</TextLink>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-6">
            <Reveal delay={0.08}>
              <div className="overflow-hidden aspect-[4/5] md:aspect-[5/6]">
                <img
                  src={IMAGES.caseStudy}
                  alt={t('caseStudy.imgAlt')}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[600ms] ease-out hover:scale-[1.04]"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="py-24 md:py-32 bg-brand-surface" data-testid="home-about">
        <div className="max-w-content mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-5 md:order-1 order-2">
            <Reveal>
              <div className="overflow-hidden aspect-[4/5] relative">
                <img
                  src={IMAGES.aboutBanner}
                  alt={t('aboutTeaser.imgAlt')}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[600ms] ease-out hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-black/0" aria-hidden="true" />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7 md:order-2 order-1">
            <Reveal delay={0.05}>
              <SectionLabel>{t('aboutTeaser.label')}</SectionLabel>
              <h2 className="mt-5 font-display text-3xl md:text-[40px] font-semibold text-brand-primary leading-tight tracking-tight">
                {t('aboutTeaser.headline')}
              </h2>
              <p className="mt-6 text-[17px] text-brand-text leading-[1.8] max-w-[68ch]">{t('aboutTeaser.body')}</p>
              <div className="mt-8">
                <TextLink to="/about" testId="about-cta">{t('cta.moreAboutJonathan', { ns: 'common' })} →</TextLink>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INSIGHTS PREVIEW */}
      <section className="py-24 md:py-32" data-testid="home-insights">
        <div className="max-w-content mx-auto px-6 md:px-8">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div className="max-w-2xl">
                <SectionLabel>{t('insightsPreview.label')}</SectionLabel>
                <p className="mt-5 font-display text-2xl md:text-[32px] font-semibold text-brand-primary leading-snug tracking-tight">
                  {t('insightsPreview.headline')}
                </p>
              </div>
              <TextLink to="/insights" testId="view-all-insights">{t('cta.viewAllInsights', { ns: 'common' })} →</TextLink>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-10 items-stretch">
            {latest.map((m, i) => (
              <Reveal key={m.slug} delay={i * 0.05}>
                <ArticleCard meta={m} testIdPrefix="home-insight" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 bg-brand-surface" data-testid="home-faq">
        <div className="max-w-content mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Reveal>
              <SectionLabel>{t('faq.label')}</SectionLabel>
              <h2 className="mt-5 font-display text-3xl md:text-[40px] font-semibold text-brand-primary leading-tight tracking-tight">
                {t('faq.headline')}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.06}>
              <Faq testIdPrefix="home-faq" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32" data-testid="home-final-cta">
        <div className="max-w-content mx-auto px-6 md:px-8 max-w-4xl">
          <Reveal>
            <SectionLabel>{t('finalCta.label')}</SectionLabel>
            <h2 className="mt-5 font-display text-3xl md:text-[52px] font-semibold text-brand-primary leading-[1.1] tracking-tight">
              {t('finalCta.headline')}
            </h2>
            <p className="mt-7 text-[17px] md:text-lg text-brand-text leading-[1.8] max-w-3xl">
              {t('finalCta.body')}
            </p>
            <div className="mt-10">
              <PrimaryButton to="/contact" testId="final-cta">{t('cta.startConversation', { ns: 'common' })}</PrimaryButton>
            </div>
            <p className="mt-8 text-sm text-brand-muted max-w-2xl">
              {t('finalCta.reassurance')}
            </p>
          </Reveal>
        </div>
      </section>
    </Layout>
  );
}
